"use client";
import { useState, useEffect } from "react";
import {
  Plus,
  GraduationCap,
  Trash2,
  Edit3,
  Check,
  X,
  BookOpen,
  Search,
} from "lucide-react";
import toast from "react-hot-toast";
import Header from "../../../components/admin/common/Header";
import PageWrapper from "../../../components/admin/common/PageWrapper";
import Sidebar from "../../../components/admin/common/Sidebar";
import Input from "../../../components/admin/common/Input";
import Button from "../../../components/admin/common/Button";
import {
  addDegree,
  getAllDegrees,
  updateDegree,
  deleteDegree,
} from "../../../services/degreeServices";
import DeleteConfirmationModal from "../../../components/admin/members/DeleteMemberModal";
import { getCookie } from "../../../utils";
import { useUserModulePermissions } from "../../../hooks/useUserModulePermissions";

// Modal Component
const Modal = ({ isOpen, onClose, title, children }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md transform transition-all duration-200">
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <h3 className="text-xl font-semibold text-gray-800">{title}</h3>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors duration-200"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
};

// Degree Form Component
const DegreeForm = ({
  initialValue = "",
  onSubmit,
  onCancel,
  isEditing = false,
}) => {
  const [degree, setDegree] = useState(initialValue);

  const handleSubmit = () => {
    if (degree.trim()) {
      onSubmit(degree.trim());
      setDegree("");
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") handleSubmit();
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
          <GraduationCap className="w-4 h-4" />
          Degree Name
        </label>
        <Input
          placeholder="e.g., Bachelor of Science"
          value={degree}
          onChange={setDegree}
          onKeyPress={handleKeyPress}
          className="focus:border-[#4f22d8] focus:ring-[#4f22d8]/20 h-11"
        />
      </div>

      <div className="flex gap-3">
        <Button
          onClick={handleSubmit}
          disabled={!degree.trim()}
          className="flex-1 bg-gradient-to-r from-[#4f22d8] to-[#3b1ba3] hover:from-[#3b1ba3] hover:to-[#2d1377] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-all duration-200 h-11 text-sm font-medium"
        >
          <Check className="w-4 h-4" />
          {isEditing ? "Update" : "Add"} Degree
        </Button>
        <Button
          onClick={onCancel}
          className="flex-1 bg-gray-600 hover:bg-gray-600 text-gray-700 border-0 h-11 text-sm font-medium transition-colors duration-200"
        >
          Cancel
        </Button>
      </div>
    </div>
  );
};

const AddDegree = () => {
  const [degrees, setDegrees] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingDegree, setEditingDegree] = useState(null);

  // Delete confirmation modal states
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [degreeToDelete, setDegreeToDelete] = useState(null);

  const fetchDegrees = async () => {
    try {
      const data = await getAllDegrees();
      setDegrees(data.data);
    } catch (error) {
      toast.error(error.message);
    }
  };

  useEffect(() => {
    fetchDegrees();
  }, []);

  const handleAdd = async (degreeName) => {
    try {
      await addDegree({ name: degreeName });
      toast.success("Degree added successfully");
      setIsModalOpen(false);
      fetchDegrees();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleEdit = async (degreeName) => {
    try {
      await updateDegree(editingDegree.value, { name: degreeName });
      toast.success("Degree updated successfully");
      setIsModalOpen(false);
      setEditingDegree(null);
      fetchDegrees();
    } catch (error) {
      toast.error(error.message);
    }
  };

  // Updated delete functions for modal
  const handleDeleteClick = (degree) => {
    setDegreeToDelete(degree);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!degreeToDelete) return;

    try {
      await deleteDegree(degreeToDelete.value);
      toast.success("Degree deleted successfully");
      fetchDegrees();
      setDeleteModalOpen(false);
      setDegreeToDelete(null);
    } catch (error) {
      toast.error(error.message);
      setDeleteModalOpen(false);
      setDegreeToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModalOpen(false);
    setDegreeToDelete(null);
  };

  const openAddModal = () => {
    setEditingDegree(null);
    setIsModalOpen(true);
  };

  const openEditModal = (degree) => {
    setEditingDegree(degree);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingDegree(null);
  };

  const filteredDegrees = degrees.filter((deg) =>
    deg.label?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const role = getCookie("role");
  const userId = getCookie("userId");

  const { data: permissionsData } = useUserModulePermissions("Degree", userId, {
    enabled: role === "maintainer" && userId && userId !== "undefined",
  });

  let canEdit = false;
  let canDelete = false;

  if (role === "admin") {
    canEdit = true;
    canDelete = true;
  } else if (role === "maintainer" && permissionsData?.data?.permissions) {
    const modulePermissions = permissionsData?.data?.permissions.find(
      (p) => p.module === "Degree"
    );

    console.log("modulePermissions found:", modulePermissions);

    if (modulePermissions) {
      canEdit = modulePermissions.edit === true;
      canDelete = modulePermissions.delete === true;
    }
  }
  const showActions = canEdit || canDelete;

  return (
    <div className="flex h-screen p-4 gap-4 bg-white overflow-hidden">
      <div className="h-full">
        <div className="sticky top-0 h-screen text-white">
          <Sidebar />
        </div>
      </div>

      <div className="flex-1 h-full bg-[#F3EFFF] rounded-[27px] overflow-hidden">
        <PageWrapper>
          <div className="flex flex-col h-full">
            <div className="flex-1 p-6 overflow-y-auto">
              <Header
                title="Degree Management"
                desc="Add and manage educational degrees"
              />

              {/* Main Content */}
              <div className="mt-8 bg-white rounded-2xl shadow-sm border border-[#4f22d8]/10">
                {/* Header Section */}
                <div className="p-6 border-b border-gray-100">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2.5 bg-gradient-to-br from-[#4f22d8] to-[#3b1ba3] rounded-xl">
                        <BookOpen className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <h2 className="text-lg font-semibold text-gray-800">
                          Degrees
                        </h2>
                        <p className="text-sm text-gray-500 mt-0.5">
                          {degrees.length} total degrees
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-4">
                      {/* Search */}
                      {degrees.length > 0 && (
                        <div className="relative">
                          <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                          <input
                            type="text"
                            placeholder="Search degrees..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="pl-12 pr-4 h-10 border border-gray-200 rounded-xl focus:border-[#4f22d8] focus:ring-2 focus:ring-[#4f22d8]/20 outline-none text-sm w-64 bg-gray-50 focus:bg-white transition-colors"
                          />
                        </div>
                      )}
                      {canEdit && (
                        <Button
                          onClick={openAddModal}
                          className="bg-gradient-to-r from-[#4f22d8] to-[#3b1ba3] hover:from-[#3b1ba3] hover:to-[#2d1377] flex items-center gap-2 h-10 px-4 text-sm font-medium"
                        >
                          <Plus className="w-4 h-4" />
                          Add Degree
                        </Button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Table Content */}
                <div className="p-6">
                  {degrees.length === 0 ? (
                    <div className="text-center py-16">
                      <div className="p-4 bg-[#4f22d8]/5 rounded-full w-16 h-16 mb-4 flex items-center justify-center mx-auto">
                        <GraduationCap className="w-8 h-8 text-[#4f22d8]" />
                      </div>
                      <h3 className="text-gray-900 font-medium text-lg mb-2">
                        No degrees added yet
                      </h3>

                      {canEdit && (
                        <>
                          <p className="text-gray-500 text-sm mb-6">
                            Create your first degree to get started
                          </p>
                          <Button
                            onClick={openAddModal}
                            className="bg-gradient-to-r from-[#4f22d8] to-[#3b1ba3] hover:from-[#3b1ba3] hover:to-[#2d1377] flex items-center gap-2 mx-auto"
                          >
                            <Plus className="w-4 h-4" />
                            Add Your First Degree
                          </Button>
                        </>
                      )}
                    </div>
                  ) : (
                    <>
                      {/* Table */}
                      <div className="overflow-hidden border border-gray-200 rounded-xl">
                        <table className="w-full">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                #
                              </th>
                              <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Degree Name
                              </th>
                              {showActions && (
                                <th className="px-6 py-4 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Actions
                                </th>
                              )}
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {filteredDegrees.map((degree, index) => (
                              <tr
                                key={degree.value}
                                className="hover:bg-gray-50 transition-colors duration-200"
                              >
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  {index + 1}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="flex items-center gap-3">
                                    <div className="p-2 bg-[#4f22d8]/10 rounded-lg">
                                      <GraduationCap className="w-4 h-4 text-[#4f22d8]" />
                                    </div>
                                    <span className="text-sm font-medium text-gray-900">
                                      {degree.label}
                                    </span>
                                  </div>
                                </td>
                                {showActions && (
                                  <td className="px-6 py-4 whitespace-nowrap text-right">
                                    <div className="flex items-center justify-end gap-2">
                                      {canEdit && (
                                        <button
                                          onClick={() => openEditModal(degree)}
                                          className="p-2 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors duration-200"
                                          title="Edit degree"
                                        >
                                          <Edit3 className="w-4 h-4 text-blue-600" />
                                        </button>
                                      )}
                                      {canDelete && (
                                        <button
                                          onClick={() =>
                                            handleDeleteClick(degree)
                                          }
                                          className="p-2 bg-red-50 hover:bg-red-100 rounded-lg transition-colors duration-200"
                                          title="Delete degree"
                                        >
                                          <Trash2 className="w-4 h-4 text-red-600" />
                                        </button>
                                      )}
                                    </div>
                                  </td>
                                )}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      {/* No Results Message */}
                      {filteredDegrees.length === 0 && searchTerm && (
                        <div className="text-center py-8">
                          <p className="text-gray-500 text-sm">
                            No degrees found matching "{searchTerm}"
                          </p>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </PageWrapper>
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={closeModal}
        title={editingDegree ? "Edit Degree" : "Add New Degree"}
      >
        <DegreeForm
          initialValue={editingDegree?.label || ""}
          onSubmit={editingDegree ? handleEdit : handleAdd}
          onCancel={closeModal}
          isEditing={!!editingDegree}
        />
      </Modal>

      <DeleteConfirmationModal
        isOpen={deleteModalOpen}
        onRequestClose={handleDeleteCancel}
        onDelete={handleDeleteConfirm}
        itemName={degreeToDelete?.label || ""}
        itemType="degree"
      />
    </div>
  );
};

export default AddDegree;
